import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { storePricingAPI } from '@/services/api';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Search, RefreshCw, Package, Save, Settings, 
  TrendingUp, TrendingDown, DollarSign, RotateCcw, 
  Percent, Check, X, IndianRupee
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const StorePricing = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({});
  const [storeInfo, setStoreInfo] = useState(null);
  const [canEditPrices, setCanEditPrices] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [filterCustomPrice, setFilterCustomPrice] = useState('all');
  
  // Bulk edit state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkMarginType, setBulkMarginType] = useState('markup_percent');
  const [bulkMarginValue, setBulkMarginValue] = useState(5);
  const [savingBulk, setSavingBulk] = useState(false);
  
  // Single edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editMarginType, setEditMarginType] = useState('fixed');
  const [editSellingPrice, setEditSellingPrice] = useState(0);
  const [editMarginValue, setEditMarginValue] = useState(0);
  const [savingEdit, setSavingEdit] = useState(false);

  const storeId = user?.entity_id;

  const fetchProducts = useCallback(async () => {
    if (!storeId) return;
    
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (search) params.search = search;
      if (selectedCategory && selectedCategory !== 'all') params.category_id = selectedCategory;
      if (selectedBrand && selectedBrand !== 'all') params.brand_id = selectedBrand;
      if (filterCustomPrice && filterCustomPrice !== 'all') {
        params.has_custom_price = filterCustomPrice === 'custom';
      }
      
      const response = await storePricingAPI.getStoreProducts(storeId, params);
      setProducts(response.data.items || []);
      setSummary(response.data.summary || {});
      setStoreInfo(response.data.store || null);
      setCanEditPrices(response.data.can_edit_prices || false);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({ title: 'Error', description: 'Failed to fetch product prices', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [storeId, search, selectedCategory, selectedBrand, filterCustomPrice, toast]);

  const fetchFilters = useCallback(async () => {
    try {
      const [catRes, brandRes] = await Promise.all([
        api.get('/categories?limit=500'),
        api.get('/brands?limit=500')
      ]);
      setCategories(catRes.data || []);
      setBrands(brandRes.data || []);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  }, []);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('all');
    setSelectedBrand('all');
    setFilterCustomPrice('all');
    setSelectedProducts([]);
  };

  const toggleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.product_id));
    }
  };

  const openEditDialog = (product) => {
    setEditProduct(product);
    setEditMarginType(product.has_custom_price ? product.margin_type : 'fixed');
    setEditSellingPrice(product.store_selling_price);
    setEditMarginValue(product.margin_value || 0);
    setEditDialogOpen(true);
  };

  // Calculate preview price based on margin type
  const calculatePreviewPrice = () => {
    if (!editProduct) return 0;
    const hqPrice = editProduct.hq_selling_price;
    
    if (editMarginType === 'markup_percent') {
      return hqPrice * (1 + editMarginValue / 100);
    } else if (editMarginType === 'markdown_percent') {
      return hqPrice * (1 - editMarginValue / 100);
    }
    return editSellingPrice;
  };

  const handleSaveSingle = async () => {
    if (!editProduct) return;
    
    setSavingEdit(true);
    try {
      const data = {
        selling_price: editMarginType === 'fixed' ? editSellingPrice : calculatePreviewPrice(),
        margin_type: editMarginType,
        margin_value: editMarginType !== 'fixed' ? editMarginValue : null
      };
      
      await storePricingAPI.updateProductPrice(storeId, editProduct.product_id, data);
      
      toast({ title: 'Success', description: `Price updated for ${editProduct.product_name}` });
      setEditDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving price:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save price', variant: 'destructive' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResetToHQ = async (product) => {
    if (!product.has_custom_price) return;
    
    try {
      await storePricingAPI.deleteCustomPrice(storeId, product.product_id);
      toast({ title: 'Success', description: `Reset to HQ price for ${product.product_name}` });
      fetchProducts();
    } catch (error) {
      console.error('Error resetting price:', error);
      toast({ title: 'Error', description: 'Failed to reset price', variant: 'destructive' });
    }
  };

  const handleBulkSave = async () => {
    if (selectedProducts.length === 0) return;
    
    setSavingBulk(true);
    try {
      await storePricingAPI.bulkUpdatePrices(storeId, {
        product_ids: selectedProducts,
        margin_type: bulkMarginType,
        margin_value: bulkMarginValue
      });
      
      toast({ 
        title: 'Success', 
        description: `Updated prices for ${selectedProducts.length} products` 
      });
      setBulkDialogOpen(false);
      setSelectedProducts([]);
      fetchProducts();
    } catch (error) {
      console.error('Error bulk updating:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update prices', variant: 'destructive' });
    } finally {
      setSavingBulk(false);
    }
  };

  const handleBulkReset = async () => {
    if (selectedProducts.length === 0) return;
    
    try {
      await storePricingAPI.resetToHQPrices(storeId, { product_ids: selectedProducts });
      toast({ title: 'Success', description: `Reset ${selectedProducts.length} products to HQ prices` });
      setSelectedProducts([]);
      fetchProducts();
    } catch (error) {
      console.error('Error resetting prices:', error);
      toast({ title: 'Error', description: 'Failed to reset prices', variant: 'destructive' });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  return (
    <div className="p-6 space-y-6" data-testid="store-pricing-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <IndianRupee className="w-6 h-6 text-emerald-600" />
            Price Variance
          </h1>
          <p className="text-gray-500 mt-1">
            {canEditPrices 
              ? 'Manage selling price variance from HQ prices with custom markup/markdown'
              : 'View product prices (editing available for franchise stores only)'}
          </p>
        </div>
        <div className="flex gap-2">
          {selectedProducts.length > 0 && canEditPrices && (
            <>
              <Button 
                onClick={handleBulkReset}
                variant="outline"
                className="text-gray-600"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset ({selectedProducts.length})
              </Button>
              <Button 
                onClick={() => setBulkDialogOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="bulk-edit-btn"
              >
                <Percent className="w-4 h-4 mr-2" />
                Bulk Edit ({selectedProducts.length})
              </Button>
            </>
          )}
          <Button onClick={fetchProducts} variant="outline" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Products</p>
                <p className="text-xl font-bold text-gray-900">{summary.total_products || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Custom Prices</p>
                <p className="text-xl font-bold text-emerald-600">{summary.products_with_custom_price || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Using HQ Default</p>
                <p className="text-xl font-bold text-gray-900">{summary.using_hq_default || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Check className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Selected</p>
                <p className="text-xl font-bold text-gray-900">{selectedProducts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Store Info Banner */}
      {storeInfo && (
        <Card className={`border-l-4 ${storeInfo.is_franchise ? 'border-l-emerald-500 bg-emerald-50/50' : 'border-l-gray-400 bg-gray-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{storeInfo.name} ({storeInfo.code})</p>
                <p className="text-sm text-gray-500">
                  {storeInfo.is_franchise 
                    ? 'Franchise Store - You can set custom selling prices with markup/markdown'
                    : 'Company Owned Store - Prices are managed by HQ'}
                </p>
              </div>
              <Badge className={storeInfo.is_franchise ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                {storeInfo.is_franchise ? 'Franchise' : 'Company Owned'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-gray-600 text-sm mb-1 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Product name or SKU..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-gray-600 text-sm mb-1 block">Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-gray-600 text-sm mb-1 block">Brand</Label>
              <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                <SelectTrigger>
                  <SelectValue placeholder="All Brands" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map(brand => (
                    <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-gray-600 text-sm mb-1 block">Price Type</Label>
              <Select value={filterCustomPrice} onValueChange={setFilterCustomPrice}>
                <SelectTrigger>
                  <SelectValue placeholder="All Products" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Products</SelectItem>
                  <SelectItem value="custom">Custom Price</SelectItem>
                  <SelectItem value="default">HQ Default</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button type="submit" data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            
            <Button type="button" variant="outline" onClick={clearFilters}>
              Clear
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader className="border-b bg-gray-50/50 py-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-emerald-600" />
            Product Prices
          </CardTitle>
          <CardDescription>
            {canEditPrices 
              ? 'Set custom selling prices for your franchise store'
              : 'Product selling prices at your store'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  {canEditPrices && (
                    <TableHead className="w-[50px]">
                      <Checkbox 
                        checked={selectedProducts.length === products.length && products.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                  )}
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">MRP</TableHead>
                  <TableHead className="text-right">HQ Price</TableHead>
                  <TableHead className="text-right">Store Price</TableHead>
                  <TableHead className="text-center">Margin</TableHead>
                  <TableHead className="text-center">Type</TableHead>
                  {canEditPrices && <TableHead className="text-center">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={canEditPrices ? 8 : 7} className="text-center py-8 text-gray-500">
                      Loading products...
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEditPrices ? 8 : 7} className="text-center py-8 text-gray-500">
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((item) => (
                    <TableRow 
                      key={item.product_id}
                      className={item.has_custom_price ? 'bg-emerald-50/30' : ''}
                    >
                      {canEditPrices && (
                        <TableCell>
                          <Checkbox 
                            checked={selectedProducts.includes(item.product_id)}
                            onCheckedChange={() => toggleSelectProduct(item.product_id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{item.product_name}</p>
                          <p className="text-sm text-gray-500">{item.sku}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-gray-600">
                        {formatCurrency(item.mrp)}
                      </TableCell>
                      <TableCell className="text-right text-gray-600">
                        {formatCurrency(item.hq_selling_price)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        <span className={item.has_custom_price ? 'text-emerald-700' : 'text-gray-900'}>
                          {formatCurrency(item.store_selling_price)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.margin_from_hq !== 0 ? (
                          <div className="flex items-center justify-center gap-1">
                            {item.margin_from_hq > 0 ? (
                              <TrendingUp className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-600" />
                            )}
                            <span className={item.margin_from_hq > 0 ? 'text-emerald-600' : 'text-red-600'}>
                              {item.margin_from_hq > 0 ? '+' : ''}{item.margin_percent_from_hq.toFixed(1)}%
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.has_custom_price ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {item.margin_type === 'markup_percent' ? 'Markup' : 
                             item.margin_type === 'markdown_percent' ? 'Markdown' : 'Custom'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">
                            HQ Default
                          </Badge>
                        )}
                      </TableCell>
                      {canEditPrices && (
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => openEditDialog(item)}
                              data-testid={`edit-btn-${item.product_id}`}
                            >
                              <Settings className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            {item.has_custom_price && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleResetToHQ(item)}
                                className="text-gray-500"
                                title="Reset to HQ price"
                              >
                                <RotateCcw className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Single Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-emerald-600" />
              Edit Product Price
            </DialogTitle>
            <DialogDescription>
              Set custom selling price for this product
            </DialogDescription>
          </DialogHeader>
          
          {editProduct && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">{editProduct.product_name}</p>
                <p className="text-sm text-gray-500">SKU: {editProduct.sku}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">MRP:</span> {formatCurrency(editProduct.mrp)}
                  </div>
                  <div>
                    <span className="text-gray-500">HQ Price:</span> {formatCurrency(editProduct.hq_selling_price)}
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Pricing Method</Label>
                <Select value={editMarginType} onValueChange={setEditMarginType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="fixed">Fixed Price</SelectItem>
                    <SelectItem value="markup_percent">Markup % (above HQ price)</SelectItem>
                    <SelectItem value="markdown_percent">Markdown % (below HQ price)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {editMarginType === 'fixed' ? (
                <div className="space-y-2">
                  <Label>Selling Price</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editSellingPrice}
                    onChange={(e) => setEditSellingPrice(parseFloat(e.target.value) || 0)}
                    placeholder="Enter price"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>
                    {editMarginType === 'markup_percent' ? 'Markup Percentage' : 'Markdown Percentage'}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={editMarginValue}
                      onChange={(e) => setEditMarginValue(parseFloat(e.target.value) || 0)}
                      placeholder="Enter %"
                    />
                    <span className="text-gray-500">%</span>
                  </div>
                </div>
              )}
              
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-700">
                  <strong>Preview Price:</strong> {formatCurrency(calculatePreviewPrice())}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  {editMarginType === 'markup_percent' 
                    ? `HQ Price + ${editMarginValue}%`
                    : editMarginType === 'markdown_percent'
                    ? `HQ Price - ${editMarginValue}%`
                    : 'Fixed custom price'}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSingle} 
              disabled={savingEdit}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingEdit ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Price
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-emerald-600" />
              Bulk Price Update
            </DialogTitle>
            <DialogDescription>
              Apply markup or markdown to {selectedProducts.length} selected products
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                <strong>{selectedProducts.length}</strong> products selected
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Pricing Method</Label>
              <Select value={bulkMarginType} onValueChange={setBulkMarginType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="markup_percent">Markup % (above HQ price)</SelectItem>
                  <SelectItem value="markdown_percent">Markdown % (below HQ price)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>
                {bulkMarginType === 'markup_percent' ? 'Markup Percentage' : 'Markdown Percentage'}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={bulkMarginValue}
                  onChange={(e) => setBulkMarginValue(parseFloat(e.target.value) || 0)}
                  placeholder="Enter %"
                />
                <span className="text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500">
                {bulkMarginType === 'markup_percent' 
                  ? 'Products will be priced at HQ price + this %'
                  : 'Products will be priced at HQ price - this %'}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkSave} 
              disabled={savingBulk}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingBulk ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Apply to All
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorePricing;
